import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import postService from '../services/postService';
import immigrationConsultancyService from '../services/immigrationConsultancyService';

const VisaPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [visaTypes, setVisaTypes] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [consultancyLoading, setConsultancyLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const loadPosts = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      const response = await postService.getPosts({ page: pageNum, page_size: 12, category: 'visa', ordering: '-created_at' });
      const newPosts = response.results || response;
      setHasMore(!!response.next);
      setPage(pageNum);
      setPosts(prev => (append ? [...prev, ...newPosts] : newPosts));
    } catch (e) {
      setError('Failed to load visa posts');
    } finally {
      setLoading(false);
    }
  }, []);


  const loadImmigrationConsultancy = useCallback(async () => {
    try {
      setConsultancyLoading(true);
      console.log('Loading immigration consultancy data...');
      
      const [countriesData, visaTypesData, policiesData] = await Promise.all([
        immigrationConsultancyService.getCountries(),
        immigrationConsultancyService.getVisaTypes({ popular: true }),
        immigrationConsultancyService.getPolicies({ recent: true })
      ]);
      
      console.log('Countries:', countriesData);
      console.log('Popular visa types:', visaTypesData);
      console.log('Recent policies:', policiesData);
      
      setCountries(countriesData);
      setVisaTypes(visaTypesData);
      setPolicies(policiesData);
    } catch (error) {
      console.error('Error loading immigration consultancy data:', error);
      setError('Failed to load immigration consultancy data');
    } finally {
      setConsultancyLoading(false);
    }
  }, []);

  const handleVisaDetails = (visa) => {
    // Navigate to visa details page using slug, fallback to ID if slug is not available
    const identifier = visa.slug || visa.id;
    if (!identifier) {
      console.error('No identifier available for visa:', visa);
      return;
    }
    navigate(`/visa-details/${identifier}`);
  };

  const handlePolicyDetails = (policy) => {
    // Navigate to policy details page using slug, fallback to ID if slug is not available
    const identifier = policy.slug || policy.id;
    if (!identifier) {
      console.error('No identifier available for policy:', policy);
      return;
    }
    navigate(`/policy-details/${identifier}`);
  };

  useEffect(() => { 
    loadPosts(1, false); 
    loadImmigrationConsultancy();
  }, [loadPosts, loadImmigrationConsultancy]);

  return (
    <PageLayout title="Visa">
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Hero Section - Magazine Style */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 via-blue-700/90 to-indigo-800/90"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
            <div className="text-center">
                <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Visa
                <span className="block text-3xl lg:text-4xl font-light text-blue-100 mt-2">
                  Information Hub
                </span>
              </h1>
              <p className="text-xl lg:text-2xl text-blue-50 max-w-3xl mx-auto mb-12 leading-relaxed">
                Your comprehensive guide to visa requirements, application processes, and the latest immigration updates from around the world.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/visa/contact"
                  className="inline-flex items-center px-8 py-4 bg-white text-blue-600 rounded-full font-semibold text-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-xl"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Get Expert Guidance
                </Link>
                <button className="inline-flex items-center px-8 py-4 border-2 border-white/30 text-white rounded-full font-semibold text-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-sm">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Browse Articles
                </button>
              </div>
            </div>
          </div>
        </div>






        {/* Immigration Consultancy Section */}
        {!consultancyLoading && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-green-100 to-blue-100 text-green-800 text-sm font-medium mb-6">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Immigration Consultancy
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Visa Options
                <span className="block text-green-600">Made Simple</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Get clear, easy-to-understand information about visa requirements and immigration policies from official government sources.
              </p>
            </div>

            {/* Countries Section */}
            {countries.length > 0 && (
              <div className="mb-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Popular Destinations</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {countries.slice(0, 6).map((country) => (
                    <div key={country.id} className="bg-white rounded-xl p-4 text-center shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-3xl mb-2">{country.flag_emoji}</div>
                      <h4 className="font-semibold text-gray-900 text-sm">{country.name}</h4>
                      <p className="text-xs text-gray-600">{country.visa_types_count} visa types</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Popular Visa Types */}
            {visaTypes.length > 0 && (
              <div className="mb-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Popular Visa Types</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {visaTypes.slice(0, 6).map((visa) => (
                    <div key={visa.id} className="group bg-white rounded-3xl shadow-xl overflow-hidden border-0 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 cursor-pointer flex flex-col h-full">
                      {/* Visa Image */}
                      <div className="relative h-56 overflow-hidden">
                       <img
                         src={immigrationConsultancyService.getVisaImage(visa.category, visa.country_code)}
                          alt={`${visa.name} - ${visa.country_name}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                         onError={(e) => immigrationConsultancyService.handleImageError(e, visa.country_code)}
                          onLoad={(e) => {
                            e.target.style.opacity = '1';
                          }}
                          style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
                        />
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                        
                        {/* Category Badge */}
                        <div className="absolute top-6 left-6">
                          <span className={`inline-flex items-center px-4 py-2 rounded-2xl text-xs font-bold ${immigrationConsultancyService.getCategoryColor(visa.category)} shadow-lg backdrop-blur-sm`}>
                            {visa.category.toUpperCase()}
                          </span>
                        </div>
                        
                        {/* Country Badge */}
                        <div className="absolute top-6 right-6">
                          <span className="inline-flex items-center px-4 py-2 rounded-2xl bg-white/20 backdrop-blur-md text-white text-sm font-semibold border border-white/30">
                            {visa.country_flag} {visa.country_name}
                          </span>
                        </div>
                        
                        {/* Title Overlay */}
                        <div className="absolute bottom-6 left-6 right-6">
                          <h4 className="text-lg font-bold text-white leading-tight drop-shadow-lg">
                            {visa.name}
                          </h4>
                        </div>
                      </div>
                      
                      <div className="p-8 flex flex-col flex-grow">
                        <p className="text-gray-600 text-sm mb-6 line-clamp-2 leading-relaxed">
                          {visa.description}
                        </p>
                        
                        {/* Key Information */}
                        <div className="space-y-4 mb-6 flex-grow">
                          {visa.cost && (
                            <div className="flex items-center justify-between p-3 bg-green-50 rounded-2xl border border-green-100">
                              <span className="flex items-center text-gray-700">
                                <span className="text-lg mr-3">💰</span>
                                <span className="font-semibold">Cost</span>
                              </span>
                              <span className="font-bold text-green-600 text-lg">{visa.cost}</span>
                            </div>
                          )}
                          {visa.processing_time && (
                            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-2xl border border-blue-100">
                              <span className="flex items-center text-gray-700">
                                <span className="text-lg mr-3">⏰</span>
                                <span className="font-semibold">Processing</span>
                              </span>
                              <span className="font-bold text-blue-600 text-lg">{visa.processing_time}</span>
                            </div>
                          )}
                          {visa.validity_period && (
                            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-2xl border border-purple-100">
                              <span className="flex items-center text-gray-700">
                                <span className="text-lg mr-3">📅</span>
                                <span className="font-semibold">Validity</span>
                              </span>
                              <span className="font-bold text-purple-600 text-lg">{visa.validity_period}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Action Button - Fixed at bottom */}
                        <div className="flex items-center justify-center pt-4 border-t border-gray-100 mt-auto">
                          <button 
                            onClick={() => handleVisaDetails(visa)}
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 text-sm font-bold shadow-lg hover:shadow-xl transform hover:scale-105"
                          >
                            View Details
                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Policy Updates */}
            {policies.length > 0 && (
              <div className="mb-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Recent Policy Updates</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {policies.slice(0, 4).map((policy) => (
                    <div key={policy.id} className="group bg-white rounded-3xl shadow-xl overflow-hidden border-0 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 cursor-pointer flex flex-col h-full">
                      {/* Policy Image */}
                      <div className="relative h-48 overflow-hidden">
                       <img
                         src={immigrationConsultancyService.getPolicyImage(policy.priority, policy.country_code)}
                          alt={`${policy.title} - ${policy.country_name}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                         onError={(e) => immigrationConsultancyService.handleImageError(e, policy.country_code)}
                          onLoad={(e) => {
                            e.target.style.opacity = '1';
                          }}
                          style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
                        />
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                        
                        {/* Priority Badge */}
                        <div className="absolute top-6 left-6">
                          <span className={`inline-flex items-center px-4 py-2 rounded-2xl text-xs font-bold ${immigrationConsultancyService.getPriorityColor(policy.priority)} shadow-lg backdrop-blur-sm`}>
                            {policy.priority.toUpperCase()}
                          </span>
                        </div>
                        
                        {/* Country Badge */}
                        <div className="absolute top-6 right-6">
                          <span className="inline-flex items-center px-4 py-2 rounded-2xl bg-white/20 backdrop-blur-md text-white text-sm font-semibold border border-white/30">
                            {policy.country_flag} {policy.country_name}
                          </span>
                        </div>
                        
                        {/* Title Overlay */}
                        <div className="absolute bottom-6 left-6 right-6">
                          <h4 className="text-lg font-bold text-white leading-tight drop-shadow-lg line-clamp-2">
                            {policy.title}
                          </h4>
                        </div>
                      </div>
                      
                      <div className="p-8 flex flex-col flex-grow">
                        <div className="flex items-center justify-between mb-6">
                          <span className="text-sm text-gray-500 font-medium">{policy.time_ago}</span>
                          {policy.visa_type_name && (
                            <span className="inline-flex items-center px-4 py-2 rounded-2xl bg-blue-100 text-blue-800 text-xs font-bold">
                              {policy.visa_type_name}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-gray-600 text-sm mb-6 line-clamp-3 leading-relaxed">
                          {policy.summary}
                        </p>
                        
                        {/* Impact Information */}
                        {policy.impact && (
                          <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl border border-yellow-200 flex-grow">
                            <div className="flex items-start">
                              <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div>
                                <p className="text-sm font-bold text-yellow-800 mb-2">Impact:</p>
                                <p className="text-sm text-yellow-700 line-clamp-2 leading-relaxed">{policy.impact}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-center pt-4 border-t border-gray-100 mt-auto">
                          <button 
                            onClick={() => handlePolicyDetails(policy)}
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-2xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 text-sm font-bold shadow-lg hover:shadow-xl transform hover:scale-105"
                          >
                            Read Full Update
                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fallback when no consultancy data */}
            {countries.length === 0 && visaTypes.length === 0 && policies.length === 0 && (
              <div className="text-center py-16">
                <div className="bg-gray-100 rounded-lg p-8">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Immigration Information Coming Soon</h3>
                  <p className="text-gray-600 mb-4">
                    We're working to bring you comprehensive immigration information. Please check back soon!
                  </p>
                  <button
                    onClick={loadImmigrationConsultancy}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Refresh Information
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main Content Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-sky-100 text-sky-800 text-sm font-medium mb-6">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Community Posts
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Community 
              <span className="block text-sky-600">Discussions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join the conversation with our community members sharing their visa experiences and insights.
            </p>
          </div>
          
          {/* Posts Grid - Magazine Style */}
            {loading && page === 1 ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
            ) : error ? (
            <div className="text-center py-16">
              <div className="text-red-600 text-xl mb-4">{error}</div>
              <button 
                onClick={() => loadPosts(1, false)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Featured Post (Large) */}
              {posts.length > 0 && (
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                    <div className="relative">
                      <div className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-sky-500 to-blue-600">
                        <div className="flex items-center justify-center">
                          <svg className="w-16 h-16 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                          </svg>
                        </div>
                      </div>
                      <div className="absolute top-4 left-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/90 text-sky-800 text-sm font-medium">
                          Featured
                        </span>
                      </div>
                    </div>
                    <div className="p-8">
                      <div className="flex items-center text-sm text-gray-500 mb-4">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {new Date(posts[0].created_at).toLocaleDateString()}
                      </div>
                      <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 leading-tight">
                        {posts[0].title}
                      </h3>
                      <p className="text-gray-600 text-lg leading-relaxed mb-6">
                        {posts[0].excerpt || posts[0].content?.substring(0, 200) + '...'}
                      </p>
                      <Link
                        to={`/posts/${posts[0].slug || posts[0].id}`}
                        className="inline-flex items-center text-sky-600 font-semibold hover:text-sky-800 transition-colors"
                      >
                        Read Full Article
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Sidebar Posts */}
              <div className="space-y-6">
                {posts.slice(1, 4).map((post, index) => (
                  <div key={post.id || post.slug} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300">
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-sky-100 text-sky-800 text-xs font-medium mr-3">
                          #{index + 2}
                        </span>
                        {new Date(post.created_at).toLocaleDateString()}
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mb-3 leading-tight line-clamp-2">
                        {post.title}
                      </h4>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {post.excerpt || post.content?.substring(0, 120) + '...'}
                      </p>
                      <Link
                        to={`/posts/${post.slug || post.id}`}
                        className="inline-flex items-center text-sky-600 font-medium text-sm hover:text-sky-800 transition-colors"
                      >
                        Read More
                        <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional Posts Grid */}
          {posts.length > 4 && (
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">More Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.slice(4).map(post => (
                  <div key={post.id || post.slug} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-gray-100 to-gray-200">
                      <div className="flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {new Date(post.created_at).toLocaleDateString()}
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mb-3 leading-tight line-clamp-2">
                        {post.title}
                      </h4>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {post.excerpt || post.content?.substring(0, 120) + '...'}
                      </p>
                      <Link
                        to={`/posts/${post.slug || post.id}`}
                        className="inline-flex items-center text-sky-600 font-medium text-sm hover:text-sky-800 transition-colors"
                      >
                        Read More
                        <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              </div>
            )}

          {/* Load More Button */}
            {hasMore && !loading && (
            <div className="mt-16 text-center">
                <button
                  onClick={() => loadPosts(page + 1, true)}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-sky-600 to-blue-600 text-white rounded-full font-semibold text-lg hover:from-sky-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-xl"
                >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Load More Articles
                </button>
              </div>
            )}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 py-20">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Need Personalized Visa Guidance?
            </h2>
            <p className="text-xl text-blue-50 mb-8 max-w-2xl mx-auto">
              Get expert advice tailored to your specific situation. Our immigration specialists are here to help you navigate the visa process with confidence.
            </p>
            <Link
              to="/visa/contact"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 rounded-full font-semibold text-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Get Expert Consultation
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default VisaPage;
